import { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { confirm } from 'actions/app';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { useShowAlert } from 'hooks/useShowAlert';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import { EntityRoutes } from 'routes/constants';
import API from 'services/API';
import { canUserOrStaffEditEntity, Permissions, userHasPermission } from 'services/Authorization';
import strings from 'strings';
import { Endpoints } from 'UI/constants/endpoints';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { navigateToSendout } from 'UI/pages/Sendouts/utils';

const UNLINK_COPIES = strings.inventoryProfiles.sections.candidates.unlinkHiringDialog;

export const useHiringAuthorityPermissions = hiringAuthority => {
  const [user] = useUserContext();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  return {
    canEdit: canUserOrStaffEditEntity(user, hiringAuthority),
    canUseDailyPlan:
      checkIfFeatureFlagEnabled(FeatureFlags.DailyPlanAddTaskFromProfile) &&
      userHasPermission(Permissions.DailyPlan.CanUseModule),
    canRequestDoNotContact: checkIfFeatureFlagEnabled(FeatureFlags.RequestDoNotContactFromProfile),
    canRequestDoNotEngage: checkIfFeatureFlagEnabled(FeatureFlags.RequestDoNotEngageFromProfile),
    canSeeSendouts: checkIfFeatureFlagEnabled(FeatureFlags.NewHiringAuthorityTabs),
    canSeeJobOrders: checkIfFeatureFlagEnabled(FeatureFlags.HaJobOrderTab),
    canSeePlacements:
      userHasPermission(Permissions.Placements.CanUseModule) &&
      checkIfFeatureFlagEnabled(FeatureFlags.HaPlacementsTab),
    canSeeFeeAgreements: checkIfFeatureFlagEnabled(FeatureFlags.HaFeeAgreementTab),
    hasCandidate: Boolean(hiringAuthority.relation?.candidate_id)
  };
};

export const useHiringAuthorityNavigation = ({ id }) => {
  const history = useHistory();

  const addCandidate = () => history.push(`${EntityRoutes.CandidateCreate}?hiringId=${id}`);

  const openLinkedCandidate = linkCandidate => {
    if (!linkCandidate) return;
    history.push(EntityRoutes.CandidateProfile.replace(':id', linkCandidate.relation.candidate_id));
  };

  const openDailyPlan = () => history.push(EntityRoutes.DailyPlan);

  return {
    addCandidate,
    navigateFromSendout: navigateToSendout(history),
    openDailyPlan,
    openLinkedCandidate
  };
};

export const useUnlinkCandidate = ({ candidateId, hiringName, id, refresh }) => {
  const dispatch = useDispatch();
  const { showAlert } = useShowAlert();
  const isUnlinkingRef = useRef(false);

  const unlinkCandidate = async () => {
    if (isUnlinkingRef.current) return;
    isUnlinkingRef.current = true;

    try {
      const response = await API.delete(
        `${Endpoints.HiringAuthorities}/${id}/${Endpoints.Candidates}/${candidateId}/${Endpoints.Unlink}`
      );
      if (response?.status !== HTTPStatusCodes.NoContent) return;

      showAlert({
        title: UNLINK_COPIES.candidateUnlinked,
        body: strings.formatString(UNLINK_COPIES.unlinkedBody, { hiringName })
      });
      refresh();
    } catch (error) {
      showAlert({ title: UNLINK_COPIES.candidateError, error });
    } finally {
      isUnlinkingRef.current = false;
    }
  };

  return () => {
    if (!candidateId) return;

    dispatch(
      confirm({
        severity: 'warning',
        title: UNLINK_COPIES.title,
        message: strings.formatString(UNLINK_COPIES.message, { hiringName }),
        confirmButtonText: UNLINK_COPIES.confirmButtonText,
        cancelButtonText: UNLINK_COPIES.cancelButtonText,
        mustComplete: true,
        onConfirm: unlinkCandidate
      })
    );
  };
};
